import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarChart3, RefreshCw, ExternalLink, TrendingUp, Users, DollarSign, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalyticsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/summary/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build Dash URL
  const dashUrl = `${API_URL}/django_plotly_dash/app/AnalyticsDashboard/`;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-primary" />
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Interactive data visualizations and insights
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchSummaryData}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="default"
            onClick={() => window.open(dashUrl, '_blank')}
            data-testid="open-fullscreen-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Full Screen
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Quick Stats</TabsTrigger>
          <TabsTrigger value="interactive" data-testid="tab-interactive">Interactive Charts</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-8 bg-muted rounded w-16 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-24"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : summaryData ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card data-testid="kpi-enrollments">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Enrollments</p>
                        <h3 className="text-2xl font-bold text-primary">
                          {summaryData.total_enrollments?.toLocaleString() || 0}
                        </h3>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="kpi-learners">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Learners</p>
                        <h3 className="text-2xl font-bold text-blue-600">
                          {summaryData.total_learners?.toLocaleString() || 0}
                        </h3>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="kpi-revenue">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Revenue</p>
                        <h3 className="text-2xl font-bold text-green-600">
                          ${summaryData.total_revenue?.toLocaleString() || 0}
                        </h3>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="kpi-sections">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Active Classes</p>
                        <h3 className="text-2xl font-bold text-amber-600">
                          {summaryData.active_sections?.toLocaleString() || 0}
                        </h3>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Enrollment Status & Top Courses */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Enrollment by Status</CardTitle>
                    <CardDescription>Current enrollment distribution</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summaryData.enrollment_by_status && Object.entries(summaryData.enrollment_by_status).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant={
                              status === 'confirmed' ? 'default' :
                              status === 'pending' ? 'secondary' :
                              status === 'cancelled' ? 'destructive' :
                              'outline'
                            }>
                              {status}
                            </Badge>
                          </div>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Courses</CardTitle>
                    <CardDescription>Courses by enrollment count</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summaryData.top_courses?.map((course, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm truncate max-w-[200px]">{course.course}</span>
                          <Badge variant="outline">{course.enrollments} enrolled</Badge>
                        </div>
                      ))}
                      {(!summaryData.top_courses || summaryData.top_courses.length === 0) && (
                        <p className="text-sm text-muted-foreground">No course data available</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Grade Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Grade Distribution</CardTitle>
                  <CardDescription>Learners by grade level</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {summaryData.grade_distribution && Object.entries(summaryData.grade_distribution).map(([grade, count]) => (
                      <Badge key={grade} variant="outline" className="text-sm">
                        Grade {grade}: {count}
                      </Badge>
                    ))}
                    {(!summaryData.grade_distribution || Object.keys(summaryData.grade_distribution).length === 0) && (
                      <p className="text-sm text-muted-foreground">No grade data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">Unable to load analytics data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="interactive">
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Interactive Analytics Dashboard</CardTitle>
              <CardDescription>
                Powered by Plotly Dash - explore data with interactive charts
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="bg-muted/30 p-4 border-t">
                <p className="text-sm text-muted-foreground mb-4">
                  The interactive dashboard includes multiple tabs for:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                  {['Overview', 'Enrollments', 'Learners', 'Revenue', 'Instructors', 
                    'Attendance', 'Gamification', 'Partners', 'Franchises', 'Trends'].map((tab) => (
                    <Badge key={tab} variant="secondary" className="justify-center py-1">
                      {tab}
                    </Badge>
                  ))}
                </div>
              </div>
              <iframe
                src={dashUrl}
                className="w-full border-0"
                style={{ height: '800px' }}
                title="Analytics Dashboard"
                data-testid="dash-iframe"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
